import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API ---------------------- */
export type StandbyItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  margem_percentual?: string | null;
  preco_venda?: string | null;
  moeda?: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type StandbyMovAPI = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null; // técnico (coletor)
    departamento_origem: string | null;
    tipo: string | null; // mantém para MODAL
    os_numero?: string | null;
    coletor_nome?: string | null;
    criado_em: string; // ISO
    embarcacao?: string | null;
    comentario?: string | null;

    /** Fallback legado (se um dia vierem aqui) */
    responsaveis_nomes?: string[] | null;

    /** ✅ ONDE ESTÁ VINDO HOJE */
    empresa?: {
      id: string;
      nome_fantasia: string;
      responsaveis_ids?: number[];
      responsaveis_nomes?: string[]; // << Aqui vem "Karolinne do Carmo"
    };

    pendente_retorno?: boolean | null;
    pendente_responsavel_id?: number | null;
    pendente_responsavel_nome?: string | null;
    pendente_criado_em?: string | null;
  };
  itens: StandbyItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: any;
  data: StandbyMovAPI[];
};

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/coordenacao";
const URL = `${BASE}/standby`;
const URL_AWAIT = `https://api.eva-googlemarine.com/departamentos/suprimentos/standby/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };
  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );
  if (!m) {
    return { data: iso, hora: "" };
  }
  const [, ano, mes, dia, hora, minuto] = m;
  return { data: `${dia}/${mes}/${ano}`, hora: `${hora}:${minuto}` };
}

function formatCurrencyByCode(value: number, code?: string | null): string {
  const c = (code || "BRL").toUpperCase();
  const symbols: Record<string, string> = { USD: "$", BRL: "R$", EUR: "€" };
  const sym = symbols[c];
  const num = Number.isFinite(value) ? value : 0;

  if (sym) {
    return `${sym} ${num.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
    })}`;
  }
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: c,
    }).format(num);
  } catch {
    return `R$ ${num.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
  }
}

/** Linha preparada para a Tabela (ordem = seus headers) */
export type StandbyRowUI = [
  string, // OS
  string, // C. Responsável (coordenador)
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Preço de Venda (form.)
  string, // R. Coleta (técnico)
  string, // Data
  string // Hora
];

/** ---------------------- Normalização ---------------------- */

/** Resolve nomes do C. Responsável: prioriza movimentacao.empresa.responsaveis_nomes,
 *  depois movimentacao.responsaveis_nomes; se vazio, retorna "pendente". */
function resolveCoordenadores(m: StandbyMovAPI["movimentacao"]): string {
  const fromEmpresa = m.empresa?.responsaveis_nomes;
  const fromTop = m.responsaveis_nomes;

  const arr =
    Array.isArray(fromEmpresa) && fromEmpresa.length > 0
      ? fromEmpresa
      : Array.isArray(fromTop) && fromTop.length > 0
      ? fromTop
      : null;

  if (!arr || arr.length === 0) return "pendente";

  // Sanitiza nomes vazios/whitespace e junta por ", "
  const sane = arr.map((s) => String(s || "").trim()).filter(Boolean);
  return sane.length ? sane.join(", ") : "pendente";
}

/** Normaliza a resposta da API para linhas da lista */
function flattenToRows(movs: StandbyMovAPI[]): StandbyRowUI[] {
  const rows: StandbyRowUI[] = [];
  for (const m of movs) {
    const { data, hora } = toBRT(m.movimentacao.criado_em);
    const os = m.movimentacao.os_numero || "-";

    // ✅ Coordenador responsável agora lido do lugar correto
    const coord = resolveCoordenadores(m.movimentacao);

    const tecnico = m.movimentacao.coletor_nome || "-";

    for (const it of m.itens) {
      const qtd = Number(it.quantidade || 0);

      // prioriza preco_venda; se "pendente", mostra "pendente"
      const moeda = (it.moeda || "BRL").toUpperCase();
      let valorDisplay = "pendente";
      if (String(it.preco_venda).toLowerCase() !== "pendente") {
        const bruto = (it.preco_venda ?? it.custo_unitario ?? "0").toString();
        const n = Number(bruto);
        valorDisplay = formatCurrencyByCode(Number.isFinite(n) ? n : 0, moeda);
      }

      rows.push([
        os,
        coord, // 👈 agora sai "Karolinne do Carmo" em 4214
        it.produto.codigo,
        it.produto.nome,
        String(qtd),
        valorDisplay,
        tecnico,
        data,
        hora,
      ]);
    }
  }
  return rows;
}

/** ---------------------- Fetchers ---------------------- */
export async function fetchStandbyOnce(): Promise<{
  rows: StandbyRowUI[];
  meta: RespAPI["meta"] | null;
  rawMovs: StandbyMovAPI[];
}> {
  const res = await fetch(URL, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as RespAPI;
  if (!json.ok) return { rows: [], meta: json.meta ?? null, rawMovs: [] };
  return {
    rows: flattenToRows(json.data),
    meta: json.meta ?? null,
    rawMovs: json.data,
  };
}

/** ---------------------- Await (long-poll) ---------------------- */
type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);
    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useStandbySuprimentos() {
  const [rows, setRows] = useState<StandbyRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  // índice por OS (usado pelo modal)
  const [rawByOS, setRawByOS] = useState<
    Record<
      string,
      { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
    >
  >({});

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchStandbyOnce();
      setRows(r.rows);
      setMeta(r.meta);

      // monta o índice por OS (mantemos empresa dentro de mov para o modal, se precisar)
      const index: Record<
        string,
        { mov: StandbyMovAPI["movimentacao"]; itens: StandbyItemAPI[] }
      > = {};
      for (const m of r.rawMovs) {
        const os = m.movimentacao.os_numero || "-";
        if (!index[os]) index[os] = { mov: m.movimentacao, itens: [] };
        index[os].itens.push(...m.itens);
      }
      setRawByOS(index);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") await sleep(ERROR_BACKOFF_MS);
            } finally {
              if (awaitCtrlRef.current === ac) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null)
                awaitSupported.current = false;
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);
            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }
            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current)
          console.error("useStandbySuprimentos loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    await carregar();
  };

  return { rows, loading, refresh, meta, rawByOS };
}
